import {
  SampleData,
  FormattedSampleSeriesOptions,
  SequencedSampleSerieStatus,
  ReceivedSampleSerieStatus,
} from '../types';
import { AwaitingSampleSerieStatus } from '../types/awaiting-samples.types';
import { SampleCollectionLocation, SampleSerieType } from '@modules/study-insights/types/samples.types';

/**
 * Creates the initial result data structure with the required hierarchy
 * @param includeValidRecollect Whether to include totalValid and totalRecollect counters
 * @param options Configuration options for the formatter
 * @param statusEnum The enum containing status values to use
 */
const createResultStructure = (
  includeValidRecollect: boolean,
  options: FormattedSampleSeriesOptions,
  statusEnum: Record<string, string>,
): Record<string, any> => {
  const result: Record<string, any> = {
    total: 0,
  };

  Object.values(SampleCollectionLocation).forEach((location) => {
    // Initialize location level
    result[location] = {
      total: 0,
    };

    // Add totalValid and totalRecollect counters if needed
    if (includeValidRecollect) {
      result[location].totalValid = 0;
      result[location].totalRecollect = 0;
    }

    // Add nested type structure if needed
    if (options.includeType) {
      Object.values(SampleSerieType).forEach((type) => {
        result[location][type] = {
          total: 0,
          percentage: 0,
        };

        // Add nested status structure by type if needed
        if (options.includeStatus) {
          Object.values(statusEnum).forEach((status) => {
            result[location][type][status] = {
              total: 0,
              percentage: 0,
            };
          });
        }
      });
    }
  });

  return result;
};

/**
 * Updates a result structure with aggregate values from a data item
 * @param result The result structure to update
 * @param item The data item to process
 * @param options Configuration options
 * @param statusFilter Optional function to filter items by status
 */
const updateResultWithItem = (
  result: Record<string, any>,
  item: SampleData,
  options: FormattedSampleSeriesOptions,
  statusFilter?: (item: SampleData) => boolean,
): void => {
  // Skip if status filter is provided and the item doesn't pass
  if (statusFilter && !statusFilter(item)) {
    return;
  }

  // Update total and total counts by collection location
  result.total += item.value;
  if (result[item.collectionLocation]) {
    result[item.collectionLocation].total += item.value;
  }

  // Update type-specific counts if needed
  if (options.includeType && item?.type && result[item.collectionLocation]?.[item.type]) {
    result[item.collectionLocation][item.type].total += item.value;

    // Update status-specific counts if needed
    if (options.includeStatus && item?.status && result[item.collectionLocation][item.type][item.status]) {
      result[item.collectionLocation][item.type][item.status].total += item.value;
    }
  }
};

export const formatAwaitingSampleSeries = (
  data: SampleData[],
  options: FormattedSampleSeriesOptions = { includeStatus: true, includeType: true },
): Record<string, any> => {
  if (data.length === 0) {
    return [];
  }

  // Create result structure with no totalValid and no totalRecollect
  const result = createResultStructure(false, options, AwaitingSampleSerieStatus);

  // Process each data item
  for (const item of data) {
    updateResultWithItem(result, item, options);
  }

  calculatePercentages(result);
  return result;
};

/* tslint:disable */
export const formatReceivedSampleSeries = (
  data: SampleData[],
  options: FormattedSampleSeriesOptions = { includeStatus: true, includeType: true },
): Record<string, any> => {
  if (data.length === 0) {
    return [];
  }

  // Create result structure without totalValid or totalRecollect
  const result = createResultStructure(false, options, ReceivedSampleSerieStatus);

  // Process only items with valid ReceivedSampleSerieStatus
  for (const item of data) {
    updateResultWithItem(result, item, options, (i) => !!(i?.status && i.status in ReceivedSampleSerieStatus));
  }

  calculatePercentages(result);
  return result;
};

/* tslint:disable */
export const formatSequenceSampleSeries = (
  data: SampleData[],
  options: FormattedSampleSeriesOptions = { includeStatus: true, includeType: true },
): Record<string, any> => {
  if (data.length === 0) {
    return [];
  }

  // Create result structure with totalValid and totalRecollect
  const result = createResultStructure(true, options, SequencedSampleSerieStatus);

  // Process each data item
  for (const item of data) {
    updateResultWithItem(result, item, options, (i) => !!(i?.status && i.status in SequencedSampleSerieStatus));

    // Special summarization for valid(sequencing_complete, results_delivered) and recollect status
    if (
      options.includeType &&
      options.includeStatus &&
      item?.status &&
      item?.type &&
      item.status in SequencedSampleSerieStatus
    ) {
      if (
        item?.status === SequencedSampleSerieStatus.SEQUENCING_COMPLETE ||
        item?.status === SequencedSampleSerieStatus.RESULTS_DELIVERED
      ) {
        result[item.collectionLocation].totalValid += item.value;
      }
      if (item?.status === SequencedSampleSerieStatus.RECOLLECT) {
        result[item.collectionLocation].totalRecollect += item.value;
      }
    }
  }

  // Add VALID status as the sum of SEQUENCING_COMPLETE and RESULTS_DELIVERED for each location-type
  Object.values(SampleCollectionLocation).forEach((location) => {
    Object.values(SampleSerieType).forEach((type) => {
      const validTotal =
        (result[location]?.[type]?.[SequencedSampleSerieStatus.SEQUENCING_COMPLETE]?.total || 0) +
        (result[location]?.[type]?.[SequencedSampleSerieStatus.RESULTS_DELIVERED]?.total || 0);
      result[location][type][SequencedSampleSerieStatus.VALID] = {
        total: validTotal,
        percentage: 0,
      };
    });
  });

  calculatePercentages(result);
  return result;
};

const calculatePercentages = (obj: Record<string, any>, parentTotal?: number): void => {
  const keys = Object.keys(obj);

  if (parentTotal) {
    obj.percentage = (obj.total / parentTotal) * 100;
  }

  for (const key of keys) {
    if (key !== 'total' && key !== 'percentage' && key !== 'totalValid' && key !== 'totalRecollect') {
      calculatePercentages(obj[key], obj.total);
    }
  }
};
